import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule, Location } from '@angular/common';
import { filter } from 'rxjs';
import { MATERIAL_IMPORTS } from '../../../shared/shared-exports/material.exports';
import { Breadcrumb } from '../../../shared/components/breadcrumb/breadcrumb';
import { Sidebar } from '../../../shared/components/sidebar/sidebar';
import { AppStoreService } from '../../../store/ngrx-signal.store';
import { ENTITIES } from '../../../store/entities.registry';
import { projectModuleRoutes } from '../project-module.routing';
import { GET_PROJECTS } from '../graphql/project.graphql';
import { Project } from '../project.models';
import {
  ProjectStatus,
  projectStatusClass,
  projectStatusLabel,
  projectTypeLabel,
} from '../project.enums';
import { ProjectPhaseId, ProjectPhaseService } from '../shared/project-phase.service';

export type { ProjectPhaseId };

interface PhaseStep {
  id: ProjectPhaseId;
  label: string;
  caption: string;
  icon: string;
}

const STATUS_PHASE: Record<ProjectStatus, ProjectPhaseId> = {
  DRAFT: 'pre',
  PLANNING: 'pre',
  PROCUREMENT: 'pre',
  ACTIVE: 'during',
  ON_HOLD: 'during',
  COMPLETED: 'post',
  DEFECTS: 'post',
  CLOSED: 'post',
  CANCELLED: 'post',
};

@Component({
  selector: 'app-project-module-view',
  standalone: true,
  imports: [RouterOutlet, Breadcrumb, Sidebar, ...MATERIAL_IMPORTS, CommonModule],
  templateUrl: './project-module-view.html',
  styleUrl: './project-module-view.scss',
})
export class ProjectModuleView implements OnInit {
  menuItems = projectModuleRoutes;
  breadcrumbs: any = [];
  parentRoute: any;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly store = inject(AppStoreService);
  private readonly phaseService = inject(ProjectPhaseService);

  readonly phases: PhaseStep[] = [
    { id: 'pre', label: 'Pre-Project', caption: 'Planning & setup', icon: 'edit_note' },
    { id: 'during', label: 'During', caption: 'Implementation', icon: 'engineering' },
    { id: 'post', label: 'Post-Project', caption: 'Closure & impact', icon: 'flag' },
  ];

  readonly selectedPhase = this.phaseService.selectedPhase;

  private readonly projectId = signal<string | null>(null);
  private readonly projects = this.store.entitySignal<Project>(ENTITIES.PROJECT_VIEWED);
  private readonly seeded = signal<Project | undefined>(undefined);

  readonly project = computed<Project | undefined>(() => {
    const id = this.projectId();
    return this.projects().find((p) => p.uniqueId === id) ?? this.seeded();
  });

  readonly statusLabel = projectStatusLabel;
  readonly statusClass = projectStatusClass;
  readonly typeLabel = projectTypeLabel;

  readonly currentPhase = computed<ProjectPhaseId | null>(() => {
    const status = this.project()?.status as ProjectStatus | undefined;
    return status ? (STATUS_PHASE[status] ?? null) : null;
  });

  private phasesOf(route: { data?: Record<string, any> }): ProjectPhaseId[] {
    const p = route.data?.['phase'];
    return p ? (Array.isArray(p) ? p : [p]) : [];
  }

  readonly filteredMenuItems = computed(() =>
    this.menuItems.filter((r) => this.phasesOf(r).includes(this.selectedPhase())),
  );

  ngOnInit(): void {
    if (this.route.routeConfig) {
      const { children, ...otherRouteAttributes } = this.route.routeConfig;
      this.parentRoute = otherRouteAttributes;
    }
    const id = this.findParam('projectId');
    this.projectId.set(id);

    const passed = history.state?.['project'] as Project | undefined;
    if (passed?.uniqueId === id) this.seeded.set(passed);
    if (id) {
      this.store.query<Project>(
        ENTITIES.PROJECT_VIEWED,
        GET_PROJECTS,
        { filtering: { uniqueId: id } },
        'uniqueId',
      );
    }

    this.syncSelectedPhase();
    this.setAndUpdateBreadCrumbs();
  }

  selectPhase(phase: ProjectPhaseId): void {
    if (phase === this.selectedPhase()) return;
    this.selectedPhase.set(phase);
    const activePhases = this.phasesOf({ data: this.deepestRouteData() });
    if (activePhases.includes(phase)) return;
    const first = this.menuItems.find((r) => this.phasesOf(r).includes(phase));
    if (first?.path) this.router.navigate([first.path], { relativeTo: this.route });
  }

  private deepestRouteData(): Record<string, any> {
    let r: ActivatedRoute | null = this.route.root;
    let data: Record<string, any> = {};
    while (r) {
      if (r.snapshot.data) data = r.snapshot.data;
      r = r.firstChild;
    }
    return data;
  }

  goBack(): void {
    this.location.back();
  }

  private findParam(key: string): string | null {
    let r: ActivatedRoute | null = this.route.root;
    while (r) {
      const value = r.snapshot.paramMap.get(key);
      if (value) return value;
      r = r.firstChild;
    }
    return null;
  }

  private syncSelectedPhase(): void {
    const apply = () => {
      const phases = this.phasesOf({ data: this.deepestRouteData() });
      if (!phases.length) return;
      if (!phases.includes(this.selectedPhase())) this.selectedPhase.set(phases[0]);
    };
    apply();
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(apply);
  }

  async setAndUpdateBreadCrumbs() {
    this.breadcrumbs = await this.getBreadcrumbs(this.route);
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(async () => {
      this.breadcrumbs = await this.getBreadcrumbs(this.route);
    });
  }

  async getBreadcrumbs(route: ActivatedRoute): Promise<any> {
    const breadcrumbs: any[] = [];
    let currentRoute = route.root;
    let url = '';

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      const path = currentRoute.snapshot.url.map((s) => s.path).join('/');
      if (path) url += `/${path}`;
      const label = currentRoute?.routeConfig?.data?.['name'];

      if (label && label != breadcrumbs[breadcrumbs.length - 1]?.label) {
        breadcrumbs.push({ label, url });
      }
    }
    return breadcrumbs;
  }
}
